import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { Theme } from '@material-ui/core';
import { RotateLeft } from '@material-ui/icons';
import { TableCell, TableRow, TextField, IconButton } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { showToast } from '@klpkonsern/ui';

import {
    Amount,
    IconName,
    Illustration,
    ConfirmationDialog,
    useKlpStyles,
} from '@klp/design';
import type {
    Kostnadstype,
    IKostkategori,
    KostnadstypeRequest,
} from '@klp/prosjekt/services/types';
import { useKostnadsKalkyleContext } from '../kostnadskalkyle/context/KostnadsKalkyleProvider';
import { NumericField } from '../Vurderingsskjema/components/Form/NumericField';

const styles = (theme: Theme) => ({
    cell: {
        '&.navn': { width: '30%' },
        '&.kostkategori': { width: '30%' },
        '&.defaultCost': { width: '15%' },
        '&.defaultKostnadsforingAndel': { width: '15%' },
        '&.actions': { width: '10%' },
    },
    actionsContainer: {
        display: 'flex',
        gap: theme.spacing(1),
    },
    deletedRow: {
        '& td:not(.actions)': {
            textDecoration: 'line-through',
            color: theme.palette.text.disabled,
        },
    },
});

interface KostnadstyperTableRowProps {
    data: Kostnadstype;
    kostkategorier: Array<IKostkategori>;
    isEditing: boolean;
    onEdit?: () => void;
    onUpsert: (data: KostnadstypeRequest) => void;
    onCancel: () => void;
    isNew: boolean;
}

export const KostnadstyperTableRow: React.FC<KostnadstyperTableRowProps> = ({
    data,
    kostkategorier,
    isEditing,
    onEdit,
    onUpsert,
    onCancel,
    isNew,
}) => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const {
        deleteKostnadstype,
        restoreKostnadstype,
        refetchKostnadstyper,
        kostnadstyper,
    } = useKostnadsKalkyleContext();

    const [editedNavn, setEditedNavn] = useState(data.navn ?? '');
    const [editedKostKategoriId, setEditedKostKategoriId] = useState(
        data.kostKategoriId ?? '',
    );
    const [editedDefaultCost, setEditedDefaultCost] = useState(
        data.defaultCost ?? 0,
    );
    const [
        editedDefaultKostnadsforingAndel,
        setEditedDefaultKostnadsforingAndel,
    ] = useState(data.defaultKostnadsforingAndel ?? 0);

    const [navnError, setNavnError] = useState<string | undefined>();
    const [kostkategoriError, setKostkategoriError] = useState<
        string | undefined
    >();

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const deleteButtonRef = useRef<HTMLButtonElement>(null);

    const handleSave = async () => {
        // Check if anything changed (skip upsert if no changes)
        if (!isNew) {
            const hasChanges =
                editedNavn.trim() !== (data.navn ?? '') ||
                editedKostKategoriId !== (data.kostKategoriId ?? '') ||
                editedDefaultCost !== (data.defaultCost ?? 0) ||
                editedDefaultKostnadsforingAndel !==
                    (data.defaultKostnadsforingAndel ?? 0);

            if (!hasChanges) {
                // No changes, just cancel edit mode
                onCancel();
                return;
            }
        }

        // Validate required fields
        let hasError = false;
        if (!editedNavn || editedNavn.trim() === '') {
            setNavnError(t('prosjekt.kostnadstyper.error.navnRequired'));
            hasError = true;
        }
        if (!editedKostKategoriId || editedKostKategoriId.trim() === '') {
            setKostkategoriError(
                t('prosjekt.kostnadstyper.error.kostKategoriRequired'),
            );
            hasError = true;
        }

        // Check for duplicates
        const duplicate = kostnadstyper?.find(
            (kt) =>
                kt.id !== data.id &&
                kt.navn?.toLowerCase().trim() ===
                    editedNavn.toLowerCase().trim() &&
                kt.kostKategoriId === editedKostKategoriId,
        );
        if (duplicate) {
            setNavnError(
                t('prosjekt.kostnadstyper.error.duplicate', {
                    name: editedNavn,
                    kostkategori: editedKostKategoriId,
                }),
            );
            hasError = true;
        }

        if (hasError) {
            return;
        }

        onUpsert({
            id: isNew ? undefined : data.id,
            navn: editedNavn.trim(),
            kostKategoriId: editedKostKategoriId,
            defaultCost: editedDefaultCost,
            defaultKostnadsforingAndel: editedDefaultKostnadsforingAndel,
        });
    };

    const handleEditCancel = () => {
        setEditedNavn(data.navn ?? '');
        setEditedKostKategoriId(data.kostKategoriId ?? '');
        setEditedDefaultCost(data.defaultCost ?? 0);
        setEditedDefaultKostnadsforingAndel(
            data.defaultKostnadsforingAndel ?? 0,
        );
        onCancel();
    };

    const handleDeleteConfirm = async () => {
        await deleteKostnadstype({ id: data.id })
            .unwrap()
            .catch((_error) => {
                showToast({
                    body: t('general.error.message'),
                    type: 'error',
                });
            });
        refetchKostnadstyper();
        setShowDeleteDialog(false);
    };

    const handleRestore = async () => {
        await restoreKostnadstype({ id: data.id })
            .unwrap()
            .catch((_error) => {
                showToast({
                    body: t('general.error.message'),
                    type: 'error',
                });
            });
        refetchKostnadstyper();
    };

    const selectedKostKategori = kostkategorier.find(
        (k) => k.id === editedKostKategoriId,
    );

    const restoreAction = (
        <IconButton
            size="small"
            onClick={handleRestore}
            title={t('prosjekt.kostnadstyper.table.restore')}
            aria-label={t('prosjekt.kostnadstyper.table.restore')}
        >
            <RotateLeft color="secondary" />
        </IconButton>
    );

    const deleteAction = (
        <IconButton
            ref={deleteButtonRef}
            size="small"
            onClick={() => setShowDeleteDialog(true)}
            title={t('prosjekt.kostnadstyper.table.delete')}
            aria-label={t('prosjekt.kostnadstyper.table.delete')}
        >
            <Illustration
                icon={IconName.DELETE}
                size="small"
                klpPalette="secondary"
            />
        </IconButton>
    );

    if (isEditing) {
        return (
            <TableRow>
                <TableCell className={`${classes.cell} navn`}>
                    <TextField
                        fullWidth={true}
                        variant="outlined"
                        value={editedNavn}
                        onChange={(e) => {
                            setEditedNavn(e.target.value);
                            setNavnError(undefined);
                        }}
                        label={t('prosjekt.kostnadstyper.table.navn')}
                        error={!!navnError}
                        helperText={navnError}
                        required={true}
                    />
                </TableCell>
                <TableCell className={`${classes.cell} kostkategori`}>
                    <Autocomplete
                        options={kostkategorier}
                        getOptionLabel={(option) =>
                            `${option.id} - ${option.navn}`
                        }
                        getOptionSelected={(option, value) =>
                            option.id === value.id
                        }
                        value={selectedKostKategori ?? null}
                        onChange={(_, value) => {
                            setEditedKostKategoriId(value?.id ?? '');
                            setKostkategoriError(undefined);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label={t(
                                    'prosjekt.kostnadstyper.table.kostkategori',
                                )}
                                error={!!kostkategoriError}
                                helperText={kostkategoriError}
                                fullWidth={true}
                                required={true}
                            />
                        )}
                    />
                </TableCell>
                <TableCell className={`${classes.cell} defaultCost`}>
                    <NumericField
                        id={`defaultCost-${data.id}`}
                        isRequired={false}
                        isReadOnly={false}
                        fullWidth={true}
                        variant="outlined"
                        value={editedDefaultCost}
                        onValueChange={(value) =>
                            setEditedDefaultCost(value.floatValue ?? 0)
                        }
                    />
                </TableCell>
                <TableCell
                    className={`${classes.cell} defaultKostnadsforingAndel`}
                >
                    <NumericField
                        id={`defaultKostnadsforingAndel-${data.id}`}
                        isRequired={false}
                        isReadOnly={false}
                        fullWidth={true}
                        variant="outlined"
                        isAllowed={(v) => {
                            const { floatValue } = v;
                            return (
                                floatValue === undefined ||
                                (floatValue >= 0 && floatValue <= 100)
                            );
                        }}
                        value={editedDefaultKostnadsforingAndel}
                        onValueChange={(value) =>
                            setEditedDefaultKostnadsforingAndel(
                                value.floatValue ?? 0,
                            )
                        }
                    />
                </TableCell>
                <TableCell className={`${classes.cell} actions`}>
                    <div className={classes.actionsContainer}>
                        <IconButton
                            size="small"
                            onClick={handleSave}
                            title={t('prosjekt.kostnadstyper.table.save')}
                        >
                            <Illustration
                                icon={IconName.CHECK}
                                size="small"
                                klpPalette="secondary"
                            />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={handleEditCancel}
                            title={t('prosjekt.kostnadstyper.table.cancel')}
                        >
                            <Illustration
                                icon={IconName.CLOSE}
                                size="small"
                                klpPalette="secondary"
                            />
                        </IconButton>
                    </div>
                </TableCell>
            </TableRow>
        );
    }

    return (
        <TableRow className={data.deleted ? classes.deletedRow : undefined}>
            <TableCell className={`${classes.cell} navn`}>
                {data.navn}
            </TableCell>
            <TableCell className={`${classes.cell} kostkategori`}>
                {selectedKostKategori
                    ? `${selectedKostKategori.id} - ${selectedKostKategori.navn}`
                    : data.kostKategoriId}
            </TableCell>
            <TableCell className={`${classes.cell} defaultCost`}>
                <Amount value={data.defaultCost} />
            </TableCell>
            <TableCell className={`${classes.cell} defaultKostnadsforingAndel`}>
                <Amount value={data.defaultKostnadsforingAndel} />
            </TableCell>
            <TableCell className={`${classes.cell} actions`}>
                <div className={classes.actionsContainer}>
                    {onEdit && !data.deleted && (
                        <IconButton
                            size="small"
                            onClick={onEdit}
                            title={t('prosjekt.kostnadstyper.table.edit')}
                            aria-label={t('prosjekt.kostnadstyper.table.edit')}
                        >
                            <Illustration
                                icon={IconName.EDIT}
                                size="small"
                                klpPalette="secondary"
                            />
                        </IconButton>
                    )}
                    {!data.deleted && deleteAction}
                    {data.deleted && restoreAction}
                </div>
                <ConfirmationDialog
                    anchorRef={deleteButtonRef}
                    showDialog={showDeleteDialog}
                    dialogContent={t(
                        'prosjekt.kostnadstyper.table.deletePrompt',
                    )}
                    confirmText={t(
                        'prosjekt.kostnadstyper.table.deleteConfirm',
                    )}
                    cancelText={t('prosjekt.kostnadstyper.table.cancel')}
                    onConfirm={handleDeleteConfirm}
                    onCancel={() => setShowDeleteDialog(false)}
                    style={{ zIndex: 1400 }}
                />
            </TableCell>
        </TableRow>
    );
};
